import { PiAsset } from "../../Types/PiAsset";
import { useAppDispatch } from '../../app/hooks'
import { setFilter } from "../../Slices/StopCodingSlice";
import { useStopCodingDispatch } from "../OI/redux/ReduxContext";
import { StopCodingFilterType } from "../OI/redux/stopCoding";
import { ActionMenu } from "../Generic/SideMenu/ActionMenu";
import { TreeView } from "../Generic/SideMenu/TreeView";
import { usePersistedOpenItems } from "../Generic/SideMenu/usePersistedOpenItems";
import { formatAssetLabel, getAssetCapabilities, getAssetChildren, isAssetMuted } from "../AssetTreeShared";
import {
    STOP_CODING_ASSET_TREE_STORAGE_KEY,
    STOP_CODING_CAPABILITY_LABELS,
    getStopCodingFilter,
    stopCodingAssetId,
    stopCodingFilterParent,
} from "./stopCodingAssetMenu";

const STOP_CODING_FILTER_STORAGE_KEY = 'stopCodingFilterValue';
const STOP_CODING_FILTER_DISPLAY_STORAGE_KEY = 'stopCodingFilterDisplayValue';
const STOP_CODING_FILTER_TYPE_STORAGE_KEY = 'stopCodingFilterType';

interface StopCodingAssetTreeProps {
    asset: PiAsset;
}

export function StopCodingAssetTree({ asset }: StopCodingAssetTreeProps) {
    const dispatch = useAppDispatch();
    const stopCodingDispatch = useStopCodingDispatch();
    const [openItems, setOpenItems] = usePersistedOpenItems(STOP_CODING_ASSET_TREE_STORAGE_KEY, asset.Name);

    const setStopCodingFilter = (type: StopCodingFilterType, value: string, displayValue: string) => {
        try {
            window.localStorage.setItem(STOP_CODING_FILTER_STORAGE_KEY, value);
            window.localStorage.setItem(STOP_CODING_FILTER_DISPLAY_STORAGE_KEY, displayValue);
            window.localStorage.setItem(STOP_CODING_FILTER_TYPE_STORAGE_KEY, type);
        } catch {
            // Without storage the filter still applies, it just isn't remembered.
        }
        stopCodingDispatch({ type: 'SET_FIELD', payload: { field: 'stopCodingFilterType', value: type } });
        stopCodingDispatch({ type: 'SET_FIELD', payload: { field: 'stopCodingFilterValue', value } });
        stopCodingDispatch({ type: 'SET_FIELD', payload: { field: 'stopCodingFilterDisplayValue', value: displayValue } });
    };

    const handleCapabilityClick = (capabilityName: string, item: PiAsset, parent: PiAsset | undefined) => {
        const filter = getStopCodingFilter(capabilityName, item, parent);
        if (!filter) {
            return;
        }

        setStopCodingFilter(filter.type, filter.value, filter.displayValue);
        dispatch(setFilter({ type: filter.type, value: filter.value, displayName: filter.legacyDisplayName }));
    };

    const renderActions = (item: PiAsset, parent: PiAsset | undefined, depth: number) => {
        const capabilities = getAssetCapabilities(item).filter((capability) => STOP_CODING_CAPABILITY_LABELS[capability.Name]);
        if (depth === 0 || capabilities.length === 0) {
            return null;
        }

        return (
            <ActionMenu
                ariaLabel={`More options for ${formatAssetLabel(item)}`}
                items={capabilities.map((capability) => ({
                    label: STOP_CODING_CAPABILITY_LABELS[capability.Name],
                    onClick: () => handleCapabilityClick(capability.Name, item, stopCodingFilterParent(parent, depth)),
                }))}
            />
        );
    };

    return (
        <TreeView<PiAsset>
            items={[asset]}
            ariaLabel="Asset tree"
            getId={stopCodingAssetId}
            getChildren={getAssetChildren}
            renderLabel={formatAssetLabel}
            isMuted={isAssetMuted}
            renderActions={renderActions}
            openItems={openItems}
            onOpenChange={setOpenItems}
        />
    );
}
